// Permisos de marcado de un cliente RESIDENCIAL: la plantilla del cliente + una sección por
// línea.
//
// Antes esta pestaña resolvía UN solo ANI para todo el cliente
// (`creds.sip_user || lines[0].did_number`): un cliente con dos líneas configuraba la primera
// y la segunda salía con el default de cada categoría, sin que la pantalla lo dijera. Los
// permisos viven por ANI en `asterisk.ani_permissions` —es lo que compara el AGI— así que
// "por cliente" siempre fue, en realidad, "la primera línea".
//
// Ahora hay dos niveles:
//   · General del cliente → se guarda en `client_permissions` y se replica a todas las líneas.
//     También es lo que hereda una línea nueva al activarse.
//   · Por línea → escribe los ANIs de esa línea y no toca a las demás.
import { useState, useEffect, useCallback } from "react";
import { useConfirm } from "./ConfirmDialog";
import { permFetch, safe, mono, Matriz } from "./permisosComunes";

const ESTADO_LINEA = {
  active:    { txt: "Activa",     color: "var(--success,#22c55e)" },
  suspended: { txt: "Suspendida", color: "var(--danger,#ef4444)" },
  pending:   { txt: "Pendiente",  color: "var(--text-muted)" },
};

/* ── Sección 1: la configuración general del cliente ── */
function General({ clientId, cats, valores, nLineas, editable, onAplicado, onError }) {
  const [local, setLocal] = useState(valores);
  const [aplicar, setAplicar] = useState(true);
  const [busy, setBusy] = useState(false);
  // `window.confirm` devuelve false sin abrir nada en este navegador: ver ConfirmDialog.js.
  const [confirmar, dialogoConfirm] = useConfirm();

  useEffect(() => { setLocal(valores); }, [valores]);

  const guardar = async () => {
    const permitidas = cats.filter(c => local[c.nombre]).map(c => c.label);
    if (aplicar && nLineas > 0 && !(await confirmar(
      `Se van a PISAR los permisos de las ${nLineas} líneas de este cliente.\n\n` +
      `Quedan permitidas: ${permitidas.join(", ") || "(ninguna)"}\n\n` +
      `Lo que hayas configurado línea por línea se pierde. ¿Seguimos?`
    ))) return;
    setBusy(true);
    try {
      const r = await permFetch(`/permissions/cliente/${clientId}/bulk`, "PUT",
        { permisos: local, aplicar_a_lineas: aplicar });
      onAplicado(r);
    } catch (e) {
      onError(e.message || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  return (
    <div className="nv-card" style={{ marginBottom: 12 }}>
      {dialogoConfirm}
      <div className="nv-card-header">
        <span className="nv-card-title">Permisos generales del cliente</span>
      </div>
      <div style={{ fontSize: 11.5, color: "var(--text-secondary)", lineHeight: 1.55, marginBottom: 12 }}>
        Es la configuración del <strong>cliente</strong>: se aplica a todas sus líneas y es la que
        hereda una línea nueva al activarse. Para que una línea puntual marque distinto, editala
        abajo.
      </div>
      <Matriz cats={cats} valores={local} editable={editable && !busy}
        onChange={(k, v) => setLocal(p => ({ ...p, [k]: v }))}/>
      {editable && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11.5,
            color: "var(--text-secondary)", cursor: "pointer" }}>
            <input type="checkbox" checked={aplicar} onChange={e => setAplicar(e.target.checked)}
              style={{ width: 15, height: 15, cursor: "pointer" }}/>
            Aplicar ahora a las {nLineas} líneas (si lo destildás, solo queda para las líneas nuevas)
          </label>
          <button className="nv-btn nv-btn-primary" style={{ marginLeft: "auto" }}
            disabled={busy} onClick={guardar}>
            {busy ? "Guardando…" : "Guardar permisos generales"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Panel principal ── */
export default function PermisosCliente({ clientId, editable = true }) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  const cargar = useCallback(async () => {
    if (!clientId) return;
    setCargando(true);
    const r = await safe(() => permFetch(`/permissions/cliente/${clientId}`));
    if (!r) setErr("No se pudieron cargar los permisos");
    setData(r);
    setCargando(false);
  }, [clientId]);
  useEffect(() => { cargar(); }, [cargar]);

  if (cargando) {
    return <div className="nv-card"><div className="nv-card-header">
      <span className="nv-card-title">Permisos de marcado</span></div>
      <div style={{ padding: "28px 0", textAlign: "center", color: "var(--text-secondary)", fontSize: 12.5 }}>
        Cargando permisos…</div></div>;
  }
  if (!data) {
    return <div className="nv-card"><div className="nv-card-header">
      <span className="nv-card-title">Permisos de marcado</span></div>
      <div style={{ padding: "24px 0", textAlign: "center", color: "var(--danger)", fontSize: 12.5 }}>
        {err || "Sin datos"}</div></div>;
  }

  const { categories: cats, general, general_definida, lineas } = data;

  return (
    <div>
      {err && <div className="nv-card" style={{ marginBottom: 12, color: "var(--danger)", fontSize: 12 }}>{err}</div>}
      {msg && <div className="nv-card" style={{ marginBottom: 12, color: "var(--success)",
        fontSize: 12, fontWeight: 600 }}>✓ {msg}</div>}

      <General clientId={clientId} cats={cats} valores={general} nLineas={lineas.length} editable={editable}
        onError={(e) => setErr(e)}
        onAplicado={(r) => {
          setErr(null);
          setMsg(r?.lineas_actualizadas
            ? `Permisos generales guardados y aplicados a ${r.lineas_actualizadas} línea(s).`
            : "Permisos generales guardados. No se tocaron las líneas existentes.");
          cargar();
        }}/>

      <div className="nv-card" style={{ padding: 0 }}>
        <div className="nv-card-header" style={{ padding: "13px 16px" }}>
          <span className="nv-card-title">Líneas del cliente</span>
          <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: "auto" }}>
            {lineas.length} {lineas.length === 1 ? "línea" : "líneas"}
            {!general_definida && " · sin configuración general propia todavía"}
          </span>
        </div>

        {lineas.length === 0 ? (
          <div style={{ padding: "26px 0", textAlign: "center", color: "var(--text-muted)", fontSize: 12.5 }}>
            <div style={{ fontSize: 26, marginBottom: 8 }}>🔒</div>
            Este cliente todavía no tiene líneas. Los permisos se aplican al número con el que
            sale la llamada, así que primero hay que darle una línea en la pestaña <strong>Líneas</strong>.
          </div>
        ) : (
          <>
            <div style={{ padding: "0 16px 10px", fontSize: 11.5, color: "var(--text-secondary)" }}>
              Cómo está marcando hoy cada línea. Para cambiar UNA sola, el botón
              <strong> Permisos</strong> de esa línea en la pestaña <strong>Líneas</strong>.
            </div>
            {lineas.map(l => {
              const est = ESTADO_LINEA[l.status] || { txt: l.status, color: "var(--text-muted)" };
              const distintas = l.difiere_de_general || [];
              return (
                <div key={l.id} style={{ borderTop: "1px solid var(--border)", padding: "10px 16px",
                  display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ ...mono, fontWeight: 600, fontSize: 13 }}>
                    {l.did_number || l.ani_principal || "(sin número)"}
                  </span>
                  <span style={{ fontSize: 11, color: est.color }}>{est.txt}</span>
                  {l.plan && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>· {l.plan}</span>}
                  <span style={{ marginLeft: "auto", fontSize: 11,
                    color: distintas.length ? "var(--brand)" : "var(--text-muted)" }}>
                    {!l.ani_principal
                      ? "sin número con el que salir"
                      : !l.tiene_permisos_propios
                        ? "sin permisos propios — marca por el default de cada categoría"
                        : distintas.length === 0
                          ? "igual a la general"
                          : `${distintas.length} distinta(s) de la general`}
                  </span>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
